import React, { useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { isAutoSignature } from '../signatureFill.constants';
import { attributeTypes, Images, OrgIntegrations, useModal } from 'utils';
import { parseGoalBlocks, updateGoalBlock } from '../../utils/inlineFields';
import { GoalBlockDesignModal, goalBlockDesignModes } from '../properties/fragments/goalBlockProperties';
import { useMentionOptions } from './elements/useMentionOptions';
import { NoItemsYet, SimpleTooltip } from 'components';
import {
    InputElement,
    CheckboxElement,
    RadioElement,
    SelectElement,
    TableElement,
    SessionDataTableElement,
    AutoSignatureElement,
    DrawSignatureElement,
    UploadFileSignature,
    GraphElement,
    TargetElement,
    RichTextElement,
} from './elements';
import { FormPrevNewPage } from './formPrevNewPage';
import { useFormBuilder, formPageEnums } from '../formContext';
import { formModelEnums, getWidth } from '../../constants';
import { PrevHeaderFooterBlock } from '../../formPreview';
import { computeAttributeDelta } from 'utils/versionDelta';
import { documentVersionService } from 'store/documentVersion/documentVersion.service';

/**
 * Templates whose notes get the client's goals with their default values (`GOAL_MODE_GOALS`), so a Goal block can be filled.
 * A function, not a constant: `../../constants` is part of an import cycle with this file, so
 * `formModelEnums` is not initialised yet when this module loads.
 */
const isGoalBlockModel = (form) =>
    [formModelEnums.PROGRESSREPORT, formModelEnums.CLINICALNOTES].some(
        // Some load paths set only `models` (an array, or the flattened string), as the Goal Mode toggle allows for.
        (model) => form?.model === model || !!form?.models?.includes?.(model),
    );

export const FormBody = () => {
    const {
        formList,
        setFormList,
        selectedIndex,
        setSelectedIndex,
        activeSection,
        setActiveSection,
        activeFormPage,
        onDragStart,
        initialFormListStr,
        setIsDirty,
        previewing,
        imageUrls,
        setImageUrls,
        setSelectedInlineField,
    } = useFormBuilder();
    const currentSectionIndex = (activeSection || 1) - 1;
    const currentSection = formList?.sections?.[currentSectionIndex];
    const sectionAttributes = currentSection?.attributes || [];
    const itemRefs = useRef({});
    const { aiBridge } = OrgIntegrations();
    const { open: openModal } = useModal();
    const goalTokens = useMentionOptions({ goalMode: true });
    const targetTokens = useMentionOptions({ goalMode: true, targetMode: true });

    // ── Live block-exit capture ─────────────────────────────────────────────
    // Hook into the existing "focus a block → open Properties" state
    // (`selectedIndex`). When the active block changes, diff the block we just
    // left against the last-saved baseline (`initialFormListStr`) and POST its
    // net deltas. Diffing vs the baseline (not a selection-time snapshot) is
    // timing-independent. No-op until the form has an id and the block changed.
    const formListRef = useRef(formList);
    formListRef.current = formList;
    const trackedBlockRef = useRef(null);

    const findAttr = (form, sectionId, attributeId) =>
        form?.sections
            ?.find((s) => String(s.id) === String(sectionId))
            ?.attributes?.find((a) => String(a.id) === String(attributeId));

    useEffect(() => {
        const form = formListRef.current;
        const sectionIdx = (activeSection || 1) - 1;
        const section = form?.sections?.[sectionIdx];
        const selectedAttr = selectedIndex != null ? section?.attributes?.[selectedIndex] : null;
        const prev = trackedBlockRef.current;

        if (prev && prev.attributeId !== selectedAttr?.id && form?.id && !previewing) {
            const latest = findAttr(form, prev.sectionId, prev.attributeId);
            // Only capture if the block actually changed DURING this focus session.
            // Re-focusing an already-edited (unsaved) block without new edits must
            // not re-POST the same delta.
            const editedThisSession =
                JSON.stringify(latest ?? null) !== JSON.stringify(prev.focusValue ?? null);
            if (editedThisSession) {
                let baseline = {};
                try {
                    baseline = initialFormListStr?.current ? JSON.parse(initialFormListStr.current) : {};
                } catch {
                    baseline = {};
                }
                const baseAttr = findAttr(baseline, prev.sectionId, prev.attributeId);
                const target = {
                    entity: 'ATTRIBUTE',
                    sectionId: prev.sectionId,
                    attributeId: prev.attributeId,
                    label: prev.label,
                };
                // New block since last save (not in baseline) → capture it (with its
                // content) as an ADD; existing block → UPDATE diff; removed block →
                // skip (the save-time diff records the REMOVE).
                const changes = !latest
                    ? []
                    : baseAttr
                    ? computeAttributeDelta(baseAttr, latest, prev.sectionId, prev.label)
                    : [{ changeType: 'ADD', target, after: latest }];
                if (changes.length) {
                    documentVersionService
                        .captureChanges({ resourceModel: 'FORM', resourceId: form.id, changes })
                        .catch(() => {
                            /* versioning must never block editing */
                        });
                }
            }
        }

        trackedBlockRef.current = selectedAttr
            ? {
                  attributeId: selectedAttr.id,
                  sectionId: section?.id,
                  label: `${activeSection || 1}.${section?.name || ''}`,
                  // Value at focus-in — used to detect edits made during this session.
                  focusValue: JSON.parse(JSON.stringify(selectedAttr)),
              }
            : null;
    }, [selectedIndex, activeSection]);

    const updateSectionAttributes = (arrayOrMutator) => {
        if (typeof arrayOrMutator === 'function') {
            setFormList((prev) => {
                const currSection = prev?.sections?.[currentSectionIndex];
                const currAttrs = currSection?.attributes || [];
                const newAttrs = arrayOrMutator(currAttrs);
                const newSections = (prev?.sections || []).map((s, i) =>
                    i === currentSectionIndex ? { ...currSection, attributes: newAttrs } : s,
                );
                return { ...prev, sections: newSections };
            });
            return;
        }
        // Legacy array form — original mutate-in-place logic. Keep
        // exactly as it was pre-Option-B to avoid breaking downstream
        // dirty tracking and reference-identity assumptions.
        const newFormList = { ...formList };
        newFormList.sections[currentSectionIndex] = {
            ...currentSection,
            attributes: arrayOrMutator,
        };
        setFormList(newFormList);
    };

    const attrNormalized = useRef(false);

    useEffect(() => {
        if (attrNormalized.current) return;
        if (sectionAttributes.some((attr) => !attr.id)) {
            attrNormalized.current = true;
            const newAttributes = sectionAttributes.map((attr) => ({
                ...attr,
                id: attr.id || uuidv4(),
            }));
            updateSectionAttributes(newAttributes);
            // Re-capture baseline after UUID normalization so it doesn't falsely trigger dirty state
            if (initialFormListStr) {
                requestAnimationFrame(() => {
                    initialFormListStr.current = JSON.stringify({
                        ...formList,
                        sections: formList.sections.map((s, i) =>
                            i === currentSectionIndex ? { ...s, attributes: newAttributes } : s,
                        ),
                    });
                    setIsDirty(false);
                });
            }
        }
    }, [sectionAttributes]);

    // Rich-text value change: uses the mutator form so this closure
    // always sees the latest committed section attributes (pre-2026-
    // 07-08 array form closed over a stale reference, causing rapid
    // successive updates to overwrite each other).
    const handleValueChange = (index, value) => {
        updateSectionAttributes((currentAttrs) => {
            const newAttrs = [...currentAttrs];
            newAttrs[index] = { ...newAttrs[index], value };
            return newAttrs;
        });
    };

    // Opens a Goal block's design modal; Save writes both designs into the block's div in the text.
    const openGoalBlockDesign = (index, blockId) => {
        const findBlock = (attrs) => parseGoalBlocks(attrs?.[index]?.value).find((entry) => entry.id === blockId);
        const entry = findBlock(formListRef.current?.sections?.[currentSectionIndex]?.attributes);
        if (!entry) return;
        openModal(
            <GoalBlockDesignModal
                block={entry.block}
                initialMode={goalBlockDesignModes.GOAL}
                goalTokens={goalTokens}
                targetTokens={targetTokens}
                onSave={(designs) =>
                    updateSectionAttributes((currentAttrs) => {
                        const current = findBlock(currentAttrs);
                        if (!current) return currentAttrs;
                        const newAttrs = [...currentAttrs];
                        const value = updateGoalBlock(newAttrs[index].value, blockId, { block: { ...current.block, ...designs } });
                        newAttrs[index] = { ...newAttrs[index], value };
                        return newAttrs;
                    })
                }
            />,
        );
    };

    const renderInputs = (item, index, width) => {
        // RICH_TEXT marked as `aiSummary` is always read-only — content arrives
        // via AI streaming. Forcing readOnly here also hides the tiptap toolbar.
        // `properties.aiSummary` is an AiSummaryKind enum value (truthy) when set.
        const isAiSummary = item?.type === attributeTypes.RICH_TEXT && !!item?.properties?.aiSummary;
        const sharedProps = {
            item,
            index,
            value: item.value,
            onChange: (val) => handleValueChange(index, val),
            readOnly: !!item.properties?.readOnly || isAiSummary,
            formList,
            setFormList,
            activeSection,
            // Fresh signed URLs for rich-text `<img data-key>` refs.
            // Server-delivered on GET, held in FormBuilderContext (not
            // formList — signed URLs would false-positive the dirty
            // check). Other element types ignore the extra prop.
            imageUrls: imageUrls || {},
            // Register freshly-uploaded rich-text images into the shared
            // context so they survive the editor unmounting (switch
            // attribute and back) before the first save round-trip.
            onImageUpload: (key, url) => setImageUrls?.((prev) => ({ ...prev, [key]: url })),
        };

        switch (item?.type) {
            case attributeTypes.INPUT:
                return <InputElement {...sharedProps} />;

            case attributeTypes.RICH_TEXT:
                return (
                    <RichTextElement
                        {...sharedProps}
                        // Inline fields: not with Goal Mode (the text is repeated per goal, so
                        // its fields would be too) nor AI Summary / AI Assist (AI rewrites the whole value).
                        allowInlineFields={!item?.properties?.goalMode && !isAiSummary && !item?.properties?.aiAssist}
                        // Goal block: progress reports and session notes (the api sends both the client's goals),
                        // and not in a goal section, which already repeats per goal.
                        allowGoalBlock={isGoalBlockModel(formList) && !currentSection?.goalSection}
                        // A Goal block's chip (`openDesign`) also opens its Goal design.
                        onSelectInlineField={(fieldId, { openDesign } = {}) => {
                            setSelectedInlineField({ attributeId: item?.id, fieldId });
                            if (openDesign) openGoalBlockDesign(index, fieldId);
                        }}
                    />
                );

            case attributeTypes.CHECK_BOX:
                return <CheckboxElement {...sharedProps} />;
            case attributeTypes.RADIO_BUTTON:
                return <RadioElement {...sharedProps} />;
            case attributeTypes.SELECT:
                return <SelectElement {...sharedProps} />;
            case attributeTypes.SIGNATURE:
                // An auto-filling block has no canvas to draw on — the builder
                // shows the same line the filled form will, minus the data.
                return isAutoSignature(item?.properties) ? (
                    <AutoSignatureElement item={item} />
                ) : (
                    <DrawSignatureElement {...sharedProps} readOnly={true} />
                );
            case attributeTypes.UPLOAD:
                return <UploadFileSignature {...sharedProps} width={'100%'} blockWidth={width} />;
            case attributeTypes.TABLE:
                return <TableElement {...sharedProps} />;
            case attributeTypes.SESSION_DATA_TABLE:
                return <SessionDataTableElement {...sharedProps} readOnly={false} builderMode />;

            case attributeTypes.GRAPH:
                return <GraphElement {...sharedProps} />;
            case attributeTypes.TARGET:
                return <TargetElement {...sharedProps} />;

            default:
                return null;
        }
    };
    const handleDelete = (index) => {
        const newAttributes = [...sectionAttributes];
        newAttributes.splice(index, 1);
        updateSectionAttributes(newAttributes);
        if (selectedIndex === index) setSelectedIndex(null);
    };

    const handleDuplicate = (index) => {
        const newAttributes = [...sectionAttributes];
        const original = newAttributes[index];
        const duplicated = {
            ...JSON.parse(JSON.stringify(original)),
            id: uuidv4(),
        };
        newAttributes.splice(index + 1, 0, duplicated);
        updateSectionAttributes(newAttributes);
        setSelectedIndex(index + 1);
    };

    const handleOnDragEnd = (result) => {
        if (!result.destination) return;

        let selectedId = null;
        if (selectedIndex !== null && sectionAttributes[selectedIndex]) {
            selectedId = sectionAttributes[selectedIndex].id;
        }

        const items = Array.from(sectionAttributes);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        updateSectionAttributes(items);

        if (selectedId) {
            const newIndex = items.findIndex((item) => item.id === selectedId);
            if (newIndex !== -1 && newIndex !== selectedIndex) {
                setSelectedIndex(newIndex);
            }
        }
    };

    // Header/Footer page rendering (Form Settings is handled by the Properties panel)
    if (activeFormPage && activeFormPage !== formPageEnums.FORM_SETTINGS) {
        const pageKey = activeFormPage === formPageEnums.HEADER ? 'header' : 'footer';
        const pageData = formList?.[pageKey] || { content: '', properties: {} };

        const handleContentChange = (val) => {
            setFormList({
                ...formList,
                [pageKey]: {
                    ...pageData,
                    content: val,
                },
            });
        };

        const handleDeleteContent = () => {
            setFormList({
                ...formList,
                [pageKey]: {
                    ...pageData,
                    content: '',
                },
            });
        };

        return (
            <div className="flex-align-start full-width">
                <div className="form-body-wrapper">
                    <div className="form-body-info-banner">
                        {/*<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">*/}
                        {/*    <path*/}
                        {/*        d="M9.99984 1.33203H3.99984C3.64622 1.33203 3.30708 1.47251 3.05703 1.72256C2.80698 1.9726 2.6665 2.31174 2.6665 2.66536V13.332C2.6665 13.6857 2.80698 14.0248 3.05703 14.2748C3.30708 14.5249 3.64622 14.6654 3.99984 14.6654H11.9998C12.3535 14.6654 12.6926 14.5249 12.9426 14.2748C13.1927 14.0248 13.3332 13.6857 13.3332 13.332V4.66536L9.99984 1.33203Z"*/}
                        {/*        stroke="#0C66E4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />*/}
                        {/*    <path*/}
                        {/*        d="M9.3335 1.33203V3.9987C9.3335 4.35232 9.47397 4.69146 9.72402 4.94151C9.97407 5.19156 10.3132 5.33203 10.6668 5.33203H13.3335"*/}
                        {/*        stroke="#0C66E4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />*/}
                        {/*    <path d="M6.66683 6H5.3335" stroke="#0C66E4" stroke-width="1.5" stroke-linecap="round"*/}
                        {/*          stroke-linejoin="round" />*/}
                        {/*    <path d="M10.6668 8.66797H5.3335" stroke="#0C66E4" stroke-width="1.5" stroke-linecap="round"*/}
                        {/*          stroke-linejoin="round" />*/}
                        {/*    <path d="M10.6668 11.332H5.3335" stroke="#0C66E4" stroke-width="1.5" stroke-linecap="round"*/}
                        {/*          stroke-linejoin="round" />*/}
                        {/*</svg>*/}

                        <img
                            className="form-body-info-icon"
                            src={Images.fileAuth} alt="info"
                        />
                        <p>
                            Header and footer settings apply across the entire document and will appear on all generated
                            pages.
                            Changes here are not limited to the currently selected section.
                        </p>
                    </div>

                    <div className="form-body-item-wrapper active-item form-body-hf-editor">
                        <div className="form-body-item-box">
                            <RichTextElement
                                item={{ type: attributeTypes.RICH_TEXT, value: pageData.content }}
                                value={pageData.content}
                                onChange={handleContentChange}
                                readOnly={false}
                                index={0}
                                imageUrls={imageUrls || {}}
                                onImageUpload={(key, url) => setImageUrls?.((prev) => ({ ...prev, [key]: url }))}
                            />
                        </div>
                        {pageData.content && (
                            <div className="form-body-hf-delete-row">
                                <button
                                    className="form-body-hf-delete-btn"
                                    onClick={handleDeleteContent}
                                >
                                    <img src={Images.trash} alt="delete" />
                                    <span className="form-body-hf-delete-text">Delete</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Live Preview */}
                    <div className="form-body-hf-preview-wrapper">
                        <p className="form-body-hf-preview-title">Preview</p>
                        <div className="form-preview-page form-body-hf-preview-page">
                            <PrevHeaderFooterBlock
                                type={pageKey}
                                data={{ ...pageData, show: true }}
                                pageIndex={0}
                                totalPages={formList?.sections?.length || 1}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-align-start full-width">
            <div className="form-body-wrapper">
                <DragDropContext onDragEnd={handleOnDragEnd}>
                    <Droppable droppableId="attributes">
                        {(provided) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                style={{ display: 'flex', flexWrap: 'wrap', columnGap: '12px', width: '100%' }}
                            >
                                {sectionAttributes?.length ? (
                                    sectionAttributes?.map((item, index) => {
                                        const width = getWidth(item?.properties?.width);
                                        const isInline = item?.properties?.inline;
                                        const elementWidth = itemRefs.current[index]?.offsetWidth;
                                        const isFullWidthType = [
                                            attributeTypes.RICH_TEXT,
                                            attributeTypes.TABLE,
                                            attributeTypes.SESSION_DATA_TABLE,
                                            attributeTypes.GRAPH,
                                            attributeTypes.TARGET,
                                        ].includes(item?.type);

                                        return (
                                            <Draggable
                                                key={item.id || index}
                                                draggableId={item.id || `attribute-${index}`}
                                                index={index}
                                                isDragDisabled={onDragStart}
                                            >
                                                {(provided, snapshot) => {
                                                    return (
                                                        <div
                                                            // ref={provided.innerRef}
                                                            ref={(el) => {
                                                                provided.innerRef(el);
                                                                itemRefs.current[index] = el;
                                                            }}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            style={{
                                                                ...provided.draggableProps.style,
                                                                width: snapshot?.isDragging
                                                                    ? elementWidth
                                                                    : isFullWidthType
                                                                        ? '100%'
                                                                        : width,
                                                                boxSizing: 'border-box',
                                                                margin: '0 0 8px 0',
                                                            }}
                                                        >
                                                            <div
                                                                className={`form-body-item-wrapper ${selectedIndex === index ? 'active-item' : ''}`}
                                                                tabIndex={-1}
                                                                // onClick={() => setSelectedIndex(index)}

                                                                onClick={(e) => {
                                                                    // React bubbles clicks through portals: a click on a dropdown
                                                                    // option (popper / modal) lands here too. Blurring then closes
                                                                    // the dropdown, so ignore anything outside this item's DOM.
                                                                    if (!e.currentTarget.contains(e.target)) return;
                                                                    const clickedTag = e.target?.tagName?.toLowerCase();
                                                                    const isEditable = e.target?.isContentEditable;
                                                                    const isInsideEditor = !!e.target?.closest(
                                                                        '.tiptap-editor-wrapper, .tiptap-toolbar',
                                                                    );
                                                                    const isInsideTable = !!e.target?.closest(
                                                                        '.table-element-wrapper',
                                                                    );
                                                                    const isInput =
                                                                        clickedTag === 'input' ||
                                                                        clickedTag === 'textarea' ||
                                                                        clickedTag === 'select' ||
                                                                        clickedTag === 'button' ||
                                                                        isEditable ||
                                                                        isInsideEditor ||
                                                                        isInsideTable;

                                                                    if (!isInput) {
                                                                        document.activeElement?.blur();
                                                                        e.currentTarget.focus();
                                                                    }

                                                                    // If a Document Layout page (Page Settings / Header / Footer)
                                                                    // is active, switch back to section view so the Properties
                                                                    // panel shows item properties instead of Form Settings.
                                                                    if (activeFormPage) {
                                                                        setActiveSection(currentSectionIndex + 1);
                                                                    }

                                                                    setSelectedIndex(index);
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key !== 'Delete') return;
                                                                    if (item?.properties?.updateForbidden) return;
                                                                    const tag = e.target?.tagName?.toLowerCase();
                                                                    const isEditable = e.target?.isContentEditable;
                                                                    const isInsideEditor = !!e.target?.closest(
                                                                        '.tiptap-editor-wrapper, .tiptap-toolbar',
                                                                    );
                                                                    if (
                                                                        tag === 'input' ||
                                                                        tag === 'textarea' ||
                                                                        tag === 'select' ||
                                                                        isEditable ||
                                                                        isInsideEditor
                                                                    )
                                                                        return;
                                                                    e.preventDefault();
                                                                    handleDelete(index);
                                                                }}
                                                            >
                                                                <div className="form-body-item-box">
                                                                    <div className="space-between"
                                                                         style={{ marginBottom: 8 }}>
                                                                        <p
                                                                            className="form-body-item-label"
                                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                                                        >
                                                                            {item?.label}
                                                                            {item?.properties?.required && <span>*</span>}
                                                                            {item?.properties?.staffOnly && (
                                                                                <SimpleTooltip
                                                                                    title="Client can't edit this field"
                                                                                    placement="top"
                                                                                    arrow
                                                                                >
                                                                                    <img
                                                                                        src={Images.staffTabActive}
                                                                                        alt="staff only"
                                                                                        className="form-body-staff-only-icon"
                                                                                    />
                                                                                </SimpleTooltip>
                                                                            )}
                                                                            {aiBridge &&
                                                                                item?.type === attributeTypes.RICH_TEXT &&
                                                                                !!item?.properties?.aiSummary && (
                                                                                    <AutoAwesomeIcon
                                                                                        titleAccess="AI Summary — auto-filled by AI on note creation"
                                                                                        sx={{ fontSize: 16, color: '#0C66E4' }}
                                                                                    />
                                                                                )}
                                                                            {aiBridge &&
                                                                                item?.type === attributeTypes.RICH_TEXT &&
                                                                                !item?.properties?.aiSummary &&
                                                                                !!item?.properties?.aiAssist && (
                                                                                    <AutoAwesomeIcon
                                                                                        titleAccess="AI Assist — staff can rewrite this field via AI while filling the note"
                                                                                        sx={{ fontSize: 16, color: '#7C3AED' }}
                                                                                    />
                                                                                )}
                                                                        </p>

                                                                        {!item?.properties?.updateForbidden && (
                                                                            <div className="form-body-item-actions-group">
                                                                                <button
                                                                                    className="form-body-item-action"
                                                                                    onClick={() => handleDuplicate(index)}
                                                                                >
                                                                                    <img src={Images.duplicate} alt="duplicate" />
                                                                                </button>
                                                                                <button
                                                                                    className="form-body-item-action"
                                                                                    onClick={() => handleDelete(index)}
                                                                                >
                                                                                    <img src={Images.trash} alt="delete" />
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {renderInputs(item, index, width)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                            </Draggable>
                                        );
                                    })
                                ) : (
                                    <NoItemsYet
                                        containerStyles={{ position: 'relative', height: '75vh' }}
                                        text={'Start Building Your Template'}
                                        subTitle={'Click fields from the left panel to add them to your template'}
                                        image={Images.noDashboard}
                                    />
                                )}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                <FormPrevNewPage />
            </div>
        </div>
    );
};
